import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown'; 
import { db } from '../firebaseConfig';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext'; 
import { ReactComponent as ArrowIcon } from '../assets/icons/ArrowIcon.svg';
import LoadingSpinner from '../assets/LoadingSpinner'
import { 
collection, 
query, 
where, 
getDocs, 
doc, 
getDoc 
} from 'firebase/firestore'; 
import { useNavigate } from 'react-router-dom';
import { useDetalleHistorial } from '../hooks/useDetalleHistorial';
import { CampoCantidad } from '../components/CampoCantidad';
import { leerCache, guardarEnCache, obtenerConCache, CLAVE_VINOS, CLAVE_DETALLE } from '../services/cacheDatos';
export const formatCepas = (cepas) => {
if (!cepas || cepas.length < 2) {
return cepas;
}
const cabernetCepas = cepas.filter(cepa => cepa.startsWith('Cabernet'));
if (cabernetCepas.length >= 2) {
const cepasFormateadas = cepas.map(cepa => {
if (cepa.startsWith('Cabernet')) {
return cepa.replace('Cabernet ', ''); 
}
return cepa;
});
return cepasFormateadas;
}
return cepas;
};
// Vinos cargados antes de que existieran los campos reales 'marca' y
// 'variedad': el nombre venía como un solo texto libre (ej. "Chirz Asado
// Red" o "Red Social Malbec"), así que había que adivinar dónde cortaba la
// marca. Sólo se usa como respaldo para esos vinos viejos.
const MARCAS_DE_DOS_PALABRAS = ['Red Social'];
export const splitNombreVino = (nombre) => {
    if (!nombre) return { marca: '', resto: '' };
    const nombreTrim = nombre.trim();
    const marcaDosPalabras = MARCAS_DE_DOS_PALABRAS.find(
        marca => nombreTrim.toLowerCase().startsWith(marca.toLowerCase() + ' ')
    );
    if (marcaDosPalabras) {
        return {
            marca: nombreTrim.slice(0, marcaDosPalabras.length),
            resto: nombreTrim.slice(marcaDosPalabras.length).trim(),
        };
    }
    const partes = nombreTrim.split(' ');
    return { marca: partes[0], resto: partes.slice(1).join(' ') };
};
// Marca y variedad ahora son campos reales del vino (cargados en Druida al
// crear la etiqueta). Si el vino los tiene, se usan directo; si es un vino
// viejo sin esos campos, se cae al parche de texto libre de arriba.
export const obtenerMarcaYVariedad = (vino) => {
    if (vino?.marca) {
        return { marca: vino.marca, resto: vino.variedad || '' };
    }
    return splitNombreVino(vino?.vino_nombre);
};
// Input de cantidad propio: caja con borde dorado, un <input> de verdad
// (se puede tipear el número) y dos flechas triángulo doradas siempre
// visibles, una arriba y otra abajo. Compartido por la grilla y el detalle
// de Shop y Cupo asignado.
export const CantidadStepper = ({ value, min = 1, max, onChange, anchoDinamico = false }) => {
    const puedeSubir = max === undefined || max === null || value < max;
    const puedeBajar = value > min;
    return (
        <div className={`cantidad-stepper${anchoDinamico ? ' cantidad-stepper--dinamico' : ''}`}>
            <CampoCantidad
                className={`cantidad-stepper-input${anchoDinamico ? ' cantidad-stepper-input--dinamico' : ''}`}
                crecerConContenido={anchoDinamico}
                min={min}
                max={max}
                value={value}
                onChange={onChange}
            />
            <div className="cantidad-stepper-flechas">
                <button
                    type="button"
                    className="cantidad-stepper-flecha cantidad-stepper-arriba"
                    onClick={() => puedeSubir && onChange(value + 1)}
                    disabled={!puedeSubir}
                    aria-label="Aumentar cantidad"
                />
                <button
                    type="button"
                    className="cantidad-stepper-flecha cantidad-stepper-abajo"
                    onClick={() => puedeBajar && onChange(value - 1)}
                    disabled={!puedeBajar}
                    aria-label="Disminuir cantidad"
                />
            </div>
        </div>
    );
};
const getCupoForVino = (vinoId, cuposVinosUsuario) => {
if (!cuposVinosUsuario || cuposVinosUsuario.length === 0) {
return null;
}
const cupoEncontrado = cuposVinosUsuario.find(cupo => cupo.id === vinoId);
return cupoEncontrado ? cupoEncontrado.cupo : null;
};
// Cuentas de staff que navegan el sitio como si fueran clientes (ven vinos
// demo, Cupo asignado habilitado igual esté el flag global apagado, y
// compra de prueba sin pago real en el carrito). Martín, Tomás, Gonzalo,
// Leandro. Compartido con Shop.js, App.js y Carrito.js.
export const UIDS_CON_ACCESO_A_DEMO = ['31464078', '42876938', '34853820', '36398917'];
export const filtrarVinosDemo = (vinos, uid) => {
if (UIDS_CON_ACCESO_A_DEMO.includes(uid)) return vinos;
return vinos.filter(v => !v.id.startsWith('DEMO-'));
};
// Espera a que carguen (o fallen) todas las imágenes antes de sacar el
// loading, para no mostrar la grilla con las etiquetas apareciendo de a una.
export const precargarImagenes = (urls) => {
return Promise.all((urls || []).map((url) => new Promise((resolve) => {
if (!url) { resolve(); return; }
const img = new Image();
img.onload = resolve;
img.onerror = resolve; // una imagen rota no debe trabar el loading
img.src = url;
})));
};
// DetalleVino vive FUERA de Cupos a proposito: si se redefiniera adentro
// (como estaba antes), React ve una funcion de componente distinta en cada
// render del padre y desmonta/remonta todo el detalle -- eso es lo que hacia
// que la pantalla saltara al top al tocar "Agregar" (que dispara un re-render
// via el contexto del carrito). Como componente de modulo, la referencia es
// estable entre renders y no hay remount.
const DetalleVino = ({ vino, onVolver, cantidades, onCantidadChange, onAddToCart }) => {
const [datosOrigen, setDatosOrigen] = useState(null);
const [cargandoDetalle, setCargandoDetalle] = useState(true);
useEffect(() => {
// El origen/trazabilidad de un vino no cambia nunca (cosecha,
// embotellado, graduacion), asi que una vez traido queda guardado para
// toda la visita: entrar y salir del detalle ya no re-consulta las
// cuatro colecciones anidadas.
const claveDetalle = `${CLAVE_DETALLE}${vino?.id}`;
const cacheado = vino?.id ? leerCache(claveDetalle, Infinity) : null;
if (cacheado) {
    setDatosOrigen(cacheado.valor);
    setCargandoDetalle(false);
    return;
}
const aplicar = (datos) => {
    if (vino?.id) guardarEnCache(claveDetalle, datos);
    setDatosOrigen(datos);
};
const fetchDetallesAnidados = async () => {
if (!vino || !vino.trazabilidad_id) {
    // Sin cadena de trazabilidad (vinos demo, o cualquier vino cargado
    // sin ese vínculo): si el propio doc trae estos datos sueltos, los
    // usamos igual en vez de dejar todo en blanco.
    if (vino && (vino.fecha_cosecha || vino.fecha_embotellado || vino.graduacion_alcoholica)) {
        aplicar({
            fecha_cosecha: vino.fecha_cosecha || null,
            fecha_embotellado: vino.fecha_embotellado || null,
            graduacion_alcoholica: vino.graduacion_alcoholica ?? null,
            recipiente_utilizado: vino.recipiente_utilizado || null,
        });
    }
    setCargandoDetalle(false);
    return;
}
try {
let graduacionAlcoholica = null;
let recipienteUtilizado = null;
let fechaEmbotellado = null;
let datosViticultura = null;
const trazDocRef = doc(db, 'trazabilidad', vino.trazabilidad_id);
const trazDocSnap = await getDoc(trazDocRef);
if (!trazDocSnap.exists()) {
setCargandoDetalle(false);
return;
}
const trazData = trazDocSnap.data();
const partidasUsadas = trazData.partidas_usadas;
const embotelladoId = trazData.embotellado_id;
if (embotelladoId) {
const embotelladoDocRef = doc(db, 'embotellado', embotelladoId);
const embotelladoDocSnap = await getDoc(embotelladoDocRef);
if (embotelladoDocSnap.exists()) {
fechaEmbotellado = embotelladoDocSnap.data().estiba_inicio;
}
}
const partidaPrincipal = partidasUsadas.reduce((max, current) => {
return (current.cantidad_hl > max.cantidad_hl) ? current : max;
}, { cantidad_hl: -1 });
if (!partidaPrincipal || !partidaPrincipal.ref) {
setCargandoDetalle(false);
return;
}
const vinifDocRef = doc(db, 'vinificacion', partidaPrincipal.ref);
const vinifDocSnap = await getDoc(vinifDocRef);
if (vinifDocSnap.exists()) {
const vinifData = vinifDocSnap.data();
const loteOrigenRef = vinifData.lote_origen_ref;
graduacionAlcoholica = vinifData.guarda?.graduacion_alcoholica ?? null;
recipienteUtilizado = vinifData.guarda?.recipiente_utilizado ?? null;
if (loteOrigenRef) {
const vitDocRef = doc(db, 'viticultura', loteOrigenRef);
const vitDocSnap = await getDoc(vitDocRef);
if (vitDocSnap.exists()) {
datosViticultura = vitDocSnap.data();
}
}
}
aplicar({
...datosViticultura,
graduacion_alcoholica: graduacionAlcoholica,
recipiente_utilizado: recipienteUtilizado,
fecha_embotellado: fechaEmbotellado,
});
setCargandoDetalle(false);
} catch (error) {
console.error("Error en la carga anidada:", error);
setCargandoDetalle(false);
}
};
fetchDetallesAnidados();
}, [vino]);
const handleGoBack = () => {
    onVolver();
};
if (cargandoDetalle) return <LoadingSpinner fullScreen />;
const clasificacionFinal = vino.tipo_vino === 'Crema de Estrellas' ? vino.clasificacion_azucar : vino.clasificacion;
const cepasFormateadas = formatCepas(vino.cepas);
const contenidoCepasCompleto = vino.tipo_vino === 'Crema de Estrellas'
? 'Crema de Estrellas'
: (cepasFormateadas && cepasFormateadas.length > 0 ? cepasFormateadas.join(' - ') : null);
// Si el resto del nombre ("Malbec" en "Red Social Malbec") ya ES la
// cepa, no la repetimos en su propia línea debajo.
const { marca: marcaVino, resto: restoVino } = obtenerMarcaYVariedad(vino);
const contenidoCepas = (contenidoCepasCompleto && contenidoCepasCompleto.toLowerCase() === restoVino.toLowerCase())
? null
: contenidoCepasCompleto;
const formatStringDate = (dateString) => {
if (!dateString) return '';
try {
const dateObj = new Date(dateString + 'T00:00:00');
return dateObj.toLocaleDateString('es-ES', {
day: '2-digit',
month: '2-digit',
year: 'numeric'
});
} catch (e) {
return dateString;
}
}
const fechaCosechaFormateada = formatStringDate(datosOrigen?.fecha_cosecha);
const fechaEmbotelladoFormateada = formatStringDate(datosOrigen?.fecha_embotellado);
const graduacion = datosOrigen?.graduacion_alcoholica;
const graduacionFormateada = (typeof graduacion === 'number' && graduacion > 0) ? `${graduacion.toFixed(2)} %` : '';
return (
<div className="detalle-card" style={{paddingBottom:'100px'}}>
    {}
    <div
        className="navegacion-flecha cupo"
        onClick={handleGoBack}
    >
        <div>
            <ArrowIcon className='arrow-icon' style={{height:'15px',overflow:'visible', width:'auto',margin:'auto 0'}}/>
        </div>
        <span className="navegacion-texto">Volver a la tienda</span>
    </div>
    {}
<div className="detalle-imagen">
{vino.etiqueta_imagen_url ? (
<img
src={vino.etiqueta_imagen_url}
alt={`Etiqueta de ${vino.vino_nombre}`}
className="detalle-wine-image"
/>
) : (
<div className="image-placeholder">
<p className="data-text">Imagen no disponible</p>
</div>
)}
</div>
<div className="detalle-datos">
<h1 className="wine-name" style={{ fontWeight: 'bold', textTransform: 'uppercase', color: '#2d193b', fontSize: '25px' }}>
<span className="wine-name-marca">{marcaVino}</span>
{restoVino && (
<span className="wine-name-resto">{restoVino}</span>
)}
</h1>
{contenidoCepas && <p className="detalle-data-text" style={{fontSize:'17px'}}>{contenidoCepas}</p>}
<p className="detalle-data-text" style={{fontSize:'17px'}}>{vino.anada}</p>
{clasificacionFinal === 'Gran Reserva' && <p className="detalle-data-text" style={{fontSize:'17px', marginBottom:'20px'}}>{clasificacionFinal}</p>}
<p className="detalle-data-text" style={{color: '#a58d70', fontWeight: 'bold', textTransform:'uppercase', marginTop:'20px'}}>
Fecha de Cosecha: {fechaCosechaFormateada}
</p>
<p className="detalle-data-text" style={{color: '#a58d70', fontWeight: 'bold', textTransform:'uppercase'}}>
Fecha de Embotellado: {fechaEmbotelladoFormateada}
</p>
<p className="detalle-data-text" style={{color: '#a58d70', fontWeight: 'bold', textTransform:'uppercase'}}>
Alcohol: {graduacionFormateada}
</p>
<div className="titulo-principal" style={{margin: '40px 0 0px'}}>
<hr  className="linea-horizontal" />
<h1>NOTAS DE SU CREADOR</h1>
<hr  className="linea-horizontal" />
</div>
<div className="detalle-description-text">
<ReactMarkdown>{vino.descripcion}</ReactMarkdown>
</div>
<div className="detalle-compra">
<p className="detalle-precio">
{vino.precioUnidad ?
`AR$ ${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(vino.precioUnidad)}`
: ''}
</p>
{(() => {
const cupoDetalle = vino.cupoAsignado;
const maximoCompraDetalle = cupoDetalle !== null && cupoDetalle !== undefined
? Math.min(vino.cantidadUnidades, cupoDetalle)
: vino.cantidadUnidades;
return maximoCompraDetalle > 0 ? (
<div className='detalle-cantidad-fila'>
<CantidadStepper
value={cantidades[vino.id] || 1}
min={1}
max={maximoCompraDetalle}
onChange={(nuevoValor) => onCantidadChange(vino.id, nuevoValor)}
anchoDinamico
/>
<button
className="detalle-agregar-boton"
onClick={() => onAddToCart(vino)}
>
AGREGAR
</button>
</div>
) : (
<div className="detalle-sin-stock">
<p>CUPO ALCANZADO</p>
</div>
);
})()}
</div>
</div>
</div>
);
};
const Cupos = () => {
const navigate = useNavigate();
const { user, loading: authLoading } = useAuth(); 
const [vinos, setVinos] = useState([]);
const [cargandoVinos, setCargandoVinos] = useState(true); 
const [error, setError] = useState(null);
const [vinoSeleccionado, setVinoSeleccionado] = useState(null); 
const { addToCart } = useCart();
const [cantidades, setCantidades] = useState({});
useDetalleHistorial(!!vinoSeleccionado, () => setVinoSeleccionado(null));
useEffect(() => {
if (!authLoading && user) {
const estadoLista = user.profileData?.estadoLista;
// Solo 'Activo' tiene algo para ver acá (cubre 'Espera', 'Registrado' y
// cualquier estado desconocido con el mismo criterio: sin cupo, no hay
// nada para mostrar en Cupo asignado).
if (estadoLista !== 'Activo') {
console.log(`Estado de lista '${estadoLista}'. Redirigiendo a /perfil.`);
navigate('/perfil');
}
} else if (!authLoading && !user) {
setError("Debes iniciar sesión para ver esta colección.");
setCargandoVinos(false);
}
}, [authLoading, user, navigate]);
useEffect(() => {
if (!authLoading && user) {
let vivo = true;
const cargarVinos = async () => {
const cuposVinosUsuario = user.profileData?.cupos_vinos || [];
const vinosRef = collection(db, 'vinos');
const q = query(vinosRef, where('etiqueta_privada', '==', null));
const querySnapshot = await getDocs(q);
// Cupo asignado es la línea limitada: solo vinos a los que el ERP les
// asignó cupo al crearlos (vino.cupoAsignado > 0, el campo propio del vino
// en Firestore, no el cupo personal del cliente que se calcula más abajo).
// Los que se crearon sin cupo son stock general y viven en /shop.
const vinosData = filtrarVinosDemo(
querySnapshot.docs
.map(doc => ({ id: doc.id, ...doc.data() }))
.filter(vino => Number(vino.cupoAsignado) > 0),
user.uid
);
const stockPromises = vinosData.map(async (vino) => {
const stockDocSnap = await getDoc(doc(db, 'stock', vino.id));
let cantidadUnidades = stockDocSnap.exists() ? (stockDocSnap.data().cantidadUnidades ?? 0) : 0;
const cupoAsignado = getCupoForVino(vino.id, cuposVinosUsuario);
return {
...vino,
cantidadUnidades: cantidadUnidades,
cupoAsignado: cupoAsignado,
// Marca de origen: Carrito.js la usa para saber que este vino no está
// sujeto al mínimo de compra de 6 botellas (eso es propio de Shop).
origen: 'cupo'
};
});
const vinosConStock = await Promise.all(stockPromises);
const vinosDisponibles = vinosConStock.filter(vino => 
vino.cantidadUnidades > 0 || (vino.cupoAsignado !== null && vino.cupoAsignado > 0)
);
const vinosOrdenados = [...vinosDisponibles].sort((a, b) => a.precioUnidad - b.precioUnidad);
await precargarImagenes(vinosOrdenados.map(v => v.etiqueta_imagen_url));
return vinosOrdenados;
};
// La clave lleva el uid porque el cupo personal y el filtro de vinos demo
// dependen de quien esta mirando.
const clave = `${CLAVE_VINOS}cupo:${user.uid}`;
const cacheado = leerCache(clave);
if (cacheado) {
// Ya lo trajimos antes en esta visita: se pinta sin spinner.
setVinos(cacheado.valor);
setCargandoVinos(false);
}
if (!cacheado || !cacheado.fresco) {
obtenerConCache(clave, cargarVinos)
.then((vinosOrdenados) => {
if (!vivo) return;
setVinos(vinosOrdenados);
setError(null);
setCargandoVinos(false);
})
.catch((err) => {
console.error("Error al obtener los vinos o el stock:", err);
// Si ya habia algo en pantalla, la revalidacion que falla se traga el
// error: mejor datos de hace un rato que un cartel rojo sobre una
// lista que se ve bien.
if (!vivo || cacheado) return;
setError("Error al cargar los datos. Inténtalo de nuevo más tarde.");
setCargandoVinos(false);
});
}
return () => { vivo = false; };
} else if (!authLoading && !user) {
setError("Debes iniciar sesión para ver esta colección.");
setCargandoVinos(false);
}
return undefined;
}, [authLoading, user]);
const handleSelectVino = (vino) => {
setVinoSeleccionado(vino);
};
const handleCantidadChange = (vinoId, valor) => {
let cantidad = parseInt(valor);
const vino = vinos.find(v => v.id === vinoId);
if (!vino) return;
const cupo = vino.cupoAsignado; 
let maximoPermitido = vino.cantidadUnidades;
if (cupo !== null && cupo !== undefined) {
maximoPermitido = Math.min(vino.cantidadUnidades, cupo); 
}
if (isNaN(cantidad) || cantidad < 1) {
cantidad = 1;
}
if (cantidad > maximoPermitido) {
cantidad = maximoPermitido;
}
if (cantidad < 1) cantidad = 1;
setCantidades(prevCantidades => ({
...prevCantidades,
[vinoId]: cantidad,
}));
};
const handleAddToCart = (vino) => {
const cupo = vino.cupoAsignado; 
let maximoPermitido = vino.cantidadUnidades;
if (cupo !== null && cupo !== undefined) {
maximoPermitido = Math.min(vino.cantidadUnidades, cupo); 
}
let cantidadSeleccionada = cantidades[vino.id] || 1;
if (cantidadSeleccionada > maximoPermitido) {
cantidadSeleccionada = maximoPermitido;
}
if (maximoPermitido === 0) return;
addToCart(vino, cantidadSeleccionada);
};
if (vinoSeleccionado) {
return (
<div className="detalle-pagina-wrapper shop-detalle-wrapper historia-container">
<div className="detalle-evento-contenido">
<DetalleVino
vino={vinoSeleccionado}
onVolver={() => window.history.back()}
cantidades={cantidades}
onCantidadChange={handleCantidadChange}
onAddToCart={handleAddToCart}
/>
</div>
</div>
);
}
if (error) return <h2 className="error-text">❌ {error}</h2>;
if (authLoading || cargandoVinos) return <LoadingSpinner />;
if (vinos.length === 0) return (
<div className="historia-container">
<h2 className="contenido-centrado" style={{marginTop:'90px'}}>No hay vinos disponibles en tu colección por el momento.</h2>
</div>
);
return (
<div className="historia-container" >
<div className="contenido-scrollable">
<div className="titulo-principal">
<hr className="linea-horizontal" />
<h1>BIENVENIDO</h1>
<hr className="linea-horizontal" />
</div>
<p className="contenido-centrado">
Le damos la bienvenida oficial a la tienda exclusiva para miembros de Finca Los Astros. Este es el único espacio donde podrá acceder a nuestras creaciones de producción limitada.
<br className="spacer-10px"></br>
Cada vino que encontrará a continuación es el resultado de una cosecha y un trabajo artesanal que nos enorgullece compartir con usted.
<br className="spacer-10px"></br>
Para activar formalmente su cupo, le recordamos realizar su primera compra dentro de los próximos 30 días. Posteriormente, bastará con una compra anual para mantener su membresía activa.
<br className="spacer-10px"></br>
Lo invitamos a explorar las etiquetas disponibles y comenzar a ser parte de nuestra historia.
</p>
<div className="titulo-principal" style={{margin: '60px 0 20px'}}>
<hr style={{borderColor:'#a58d70'}} className="linea-horizontal" />
<h1 style={{color:'#a58d70'}}>NUESTRA COLECCIÓN</h1>
<hr style={{borderColor:'#a58d70'}} className="linea-horizontal" />
</div>
<div className="grid-container"> 
{vinos.map((vino) => {
const clasificacionFinal = vino.tipo_vino === 'Crema de Estrellas' ? vino.clasificacion_azucar : vino.clasificacion;
const cepasFormateadas = formatCepas(vino.cepas);
const contenidoCepasCompleto = vino.tipo_vino === 'Crema de Estrellas'
? 'Crema de Estrellas'
: (cepasFormateadas && cepasFormateadas.length > 0 ? cepasFormateadas.join(' • ') : null);
// Si el resto del nombre ("Malbec" en "Red Social Malbec") ya ES la
// cepa, no la repetimos en su propia línea debajo.
const { marca: marcaVino, resto: restoVino } = obtenerMarcaYVariedad(vino);
const contenidoCepas = (contenidoCepasCompleto && contenidoCepasCompleto.toLowerCase() === restoVino.toLowerCase())
? null
: contenidoCepasCompleto;
const cupo = vino.cupoAsignado;
const maximoCompra = cupo !== null && cupo !== undefined
? Math.min(vino.cantidadUnidades, cupo) 
: vino.cantidadUnidades;
return (
<div 
key={vino.id} 
className="card" 
>
{vino.etiqueta_imagen_url ? (
<div className="wine-image-container" >
<img 
src={vino.etiqueta_imagen_url} 
alt={`Etiqueta de ${vino.vino_nombre}`} 
className="wine-image" 
/></div>
) : (
<div className="image-placeholder">
</div>
)}
<div className="card-datos">
<h3 className="wine-name">
<span className="wine-name-marca">{marcaVino}</span>
{restoVino && (
<span className="wine-name-resto">{restoVino}</span>
)}
</h3>
{contenidoCepas && (<p className="data-text">{contenidoCepas}</p>)}
{clasificacionFinal === 'Gran Reserva' && <p className="data-text">{clasificacionFinal}</p>}
<p className="data-text">{vino.anada}</p>
<p className="data-text card-stock-anclado">
<p className="stock-units">
{`${vino.cantidadUnidades} botellas disponibles`}
</p>
</p>
</div>
<button
className="view-details-button"
onClick={() => handleSelectVino(vino)}
>
VER DETALLES
</button>
<p className="price-unit">
{vino.precioUnidad ? 
`AR$ ${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(vino.precioUnidad)}` 
: ''}
</p>
{maximoCompra > 0 ? (
<div className="grid-cantidad-fila">
<CantidadStepper
value={cantidades[vino.id] || 1}
min={1}
max={maximoCompra}
onChange={(nuevoValor) => handleCantidadChange(vino.id, nuevoValor)}
anchoDinamico
/>
<button
className="add-to-cart-button"
onClick={(e) => {
e.stopPropagation();
handleAddToCart(vino);
}}
>
AGREGAR
</button>
</div>
) : (
<div className="grid-sin-stock">
<p>CUPO ALCANZADO</p>
</div>
)}
</div>
);
})}
</div>
</div>
</div>
);
};
export default Cupos;